import React from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * DashboardGreeting — Phase 2 hero header.
 * Показва поздрав с име + date picker (prev/next ден) най-отгоре на Dashboard.
 * Всички props са опционални — работи и със standalone greeting.
 */
export default function DashboardGreeting({ selectedDate, onDateChange }) {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const firstName = (user?.profile?.personal?.firstName
    || user?.name?.split(" ")[0]
    || user?.email?.split("@")[0]
    || "").trim();

  // iter235 — Use UTC arithmetic to avoid timezone-induced "skip 2 days" bug.
  // Previous: new Date(iso + "T00:00:00") in Sofia (+3 DST) silently shifted
  // -1 day on toISOString().slice(), so each click effectively moved 2 days.
  const today = new Date().toISOString().slice(0, 10);
  const iso = selectedDate || today;
  const [yy, mm, dd] = iso.split("-").map(Number);
  const d = new Date(Date.UTC(yy, mm - 1, dd));
  const isToday = iso === today;

  const fmtDate = (date) => {
    try {
      return date.toLocaleDateString(bg ? "bg-BG" : "en-US", {
        day: "numeric", month: "long", year: "numeric", timeZone: "UTC",
      });
    } catch {
      return iso;
    }
  };

  const shift = (days) => {
    if (!onDateChange) return;
    const nd = new Date(Date.UTC(yy, mm - 1, dd + days));
    onDateChange(nd.toISOString().slice(0, 10));
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-5" data-testid="dashboard-greeting">
      <div>
        <h1 className={`text-xl sm:text-2xl font-bold tracking-tight ${isDark ? "text-white" : "text-[#0B1020]"}`}>
          {bg ? "Здравейте" : "Hello"}{firstName ? `, ${firstName}` : ""}! <span aria-hidden>👋</span>
        </h1>
        <p className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
          {bg ? "Ето какво се случва с тялото ви днес." : "Here's what's happening with your body today."}
        </p>
      </div>

      {onDateChange && (
        <div
          className={`inline-flex items-center gap-1 rounded-xl border px-1.5 py-1 text-sm self-start sm:self-auto
            ${isDark ? "bg-[hsl(222,47%,10%)] border-white/10 text-gray-200" : "bg-white border-gray-200 text-gray-800"}`}
          data-testid="dashboard-date-picker"
        >
          <button
            onClick={() => shift(-1)}
            className={`p-1.5 rounded-lg hover:opacity-80 ${isDark ? "hover:bg-white/5" : "hover:bg-gray-100"}`}
            aria-label={bg ? "Предишен ден" : "Previous day"}
            data-testid="dashboard-date-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="px-2 flex items-center gap-1.5">
            <Calendar className={`w-4 h-4 ${isDark ? "text-gray-400" : "text-gray-500"}`} />
            <span className="font-medium">
              {isToday ? (bg ? "Днес, " : "Today, ") : ""}{fmtDate(d)}
            </span>
          </div>
          <button
            onClick={() => shift(1)}
            className={`p-1.5 rounded-lg hover:opacity-80 ${isDark ? "hover:bg-white/5" : "hover:bg-gray-100"}`}
            aria-label={bg ? "Следващ ден" : "Next day"}
            data-testid="dashboard-date-next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
